import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled';
import classnames from 'classnames';
import { Mutation } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';

import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import views2 from 'four-nets/icons/svg/icon_views-2.svg';
import views from 'four-nets/icons/svg/icon_views.svg';
import msg from 'four-nets/icons/svg/icon_message-2.svg';
import friendshipSvg from 'four-nets/icons/svg/icon_friendship.svg';
import arrowDownIcon from 'four-nets/icons/svg/icon_arrow-down.svg';
import { AlertContext } from 'lib/alert-context/alert-context';
import ActionSheet, { Action } from 'four-nets/ui-v2/action-sheet/action-sheet';
import { HideForVendor, HideForUnpaidVendor } from 'four-nets/vendor/permissions';

import CoverButton from '../cover-button/cover-button';
import defaultStyles from './cover-buttons-user.scss';

import acceptFriendInvitation, {
    AcceptFriendInvitationMutation,
    AcceptFriendInvitationMutationVariables,
} from './mutations/accept-friend-invitation.graphql';

import cancelFriendInvitation, {
    CancelFriendInvitationMutation,
    CancelFriendInvitationMutationVariables,
} from './mutations/cancel-friend-invitation.graphql';

import sendFriendInvitation, {
    SendFriendInvitationMutation,
    SendFriendInvitationMutationVariables,
} from './mutations/send-friend-invitation.graphql';

import removeFriendship, {
    RemoveFriendshipMutation,
    RemoveFriendshipMutationVariables,
} from './mutations/remove-friendship.graphql';

import likeMutation, { likePhotoBlogMutation, likePhotoBlogMutationVariables } from '../../mutations/like.graphql';

import unlikeMutation, {
    unlikePhotoBlogMutation,
    unlikePhotoBlogMutationVariables,
} from '../../mutations/unlike.graphql';

import { CoverFragment } from '../cover.graphql';

// import { FriendStatus } from './mutations/accept-friend-invitation.graphql.types';
// FIXME
enum FriendStatus {
    FRIENDS = 'FRIENDS',
    NOT_FRIENDS = 'NOT_FRIENDS',
    REQUEST_PENDING = 'REQUEST_PENDING',
    WAITING_FOR_VIEWER_CONFIRMATION = 'WAITING_FOR_VIEWER_CONFIRMATION',
}

interface State {
    open: boolean;
}

export interface Props extends CoverFragment {
    styles: typeof defaultStyles;
    id: string;
    isVendor: boolean;
    writeMail: (to: string) => void;
    buttonsOnly?: boolean;
}

class CoverButtonsUser extends React.PureComponent<Props, State> {
    static contextTypes = {
        authCheck: PropTypes.func,
    };

    state = { open: false };

    open = () => {
        this.setState({ open: true });
    };

    close = () => {
        this.setState({ open: false });
    };

    render() {
        const { styles, id, user, isLikedByViewer, likesCount, isVendor, buttonsOnly } = this.props;
        const { open } = this.state;

        let userId: string;
        let username: string;
        let viewerFriendStatus = FriendStatus.NOT_FRIENDS;

        if (user) {
            userId = user.id;
            username = user.username;
            viewerFriendStatus = user.viewerFriendStatus || FriendStatus.NOT_FRIENDS;
        }

        return (
            <div className={classnames(styles.container, { [styles.mobileContainer]: buttonsOnly })}>
                <HideForUnpaidVendor style={{ relativeWrapper: styles.hideForUnpaidVendor }}>
                    <CoverButton
                        tag='button'
                        label={<FormattedMessage id='SEND_PRIVATE_MESSAGE' />}
                        onClick={() => {
                            window.location.href = `/chat/with/${username}/`
                        }}
                        iconSrc={msg}
                    />
                </HideForUnpaidVendor>
                <HideForUnpaidVendor style={{ relativeWrapper: styles.hideForUnpaidVendor }}>
                    <AlertContext.Consumer>
                        {({ confirm }) => (
                            <Mutation<likePhotoBlogMutation, likePhotoBlogMutationVariables>
                                mutation={likeMutation}
                                optimisticResponse={{
                                    likePhotoBlog: {
                                        __typename: 'LikePhotoBlogPayload',
                                        photoBlog: {
                                            __typename: 'PhotoBlogNode',
                                            id,
                                            isLikedByViewer: true,
                                            likesCount: (likesCount || 0) + 1,
                                        },
                                    },
                                }}
                            >
                                {likePhotoBlog => (
                                    <Mutation<unlikePhotoBlogMutation, unlikePhotoBlogMutationVariables>
                                        mutation={unlikeMutation}
                                        optimisticResponse={{
                                            unlikePhotoBlog: {
                                                __typename: 'UnlikePhotoBlogPayload',
                                                photoBlog: {
                                                    __typename: 'PhotoBlogNode',
                                                    id,
                                                    isLikedByViewer: false,
                                                    likesCount: (likesCount || 1) - 1,
                                                },
                                            },
                                        }}
                                    >
                                        {unlikePhotoBlog => (
                                            <CoverButton
                                                tag='button'
                                                label={
                                                    isLikedByViewer ? (
                                                        <FormattedMessage id='FOLLOWED' />
                                                    ) : (
                                                        <FormattedMessage id='FOLLOW' />
                                                    )
                                                }
                                                onClick={() =>
                                                    this.context.authCheck(() => {
                                                        if (isLikedByViewer) {
                                                            confirm({
                                                                body: (
                                                                    <FormattedMessage
                                                                        id='UNFOLLOW_USER'
                                                                        values={{ username }}
                                                                    />
                                                                ),
                                                                onAccept: () => {
                                                                    unlikePhotoBlog({ variables: { id } });
                                                                },
                                                                acceptButtonLabel: <FormattedMessage id='UNFOLLOW' />,
                                                                cancelButtonLabel: (
                                                                    <FormattedMessage id='BUTTON_LABEL_CLOSE' />
                                                                ),
                                                            });
                                                            return Promise.resolve();
                                                        } else {
                                                            return likePhotoBlog({ variables: { id } });
                                                        }
                                                    })
                                                }
                                                iconSrc={isLikedByViewer ? views2 : views}
                                            />
                                        )}
                                    </Mutation>
                                )}
                            </Mutation>
                        )}
                    </AlertContext.Consumer>
                </HideForUnpaidVendor>
                {!isVendor ? (
                    <HideForVendor>
                        <Mutation<AcceptFriendInvitationMutation, AcceptFriendInvitationMutationVariables>
                            mutation={acceptFriendInvitation}
                        >
                            {acceptFriendInvitationFn => (
                                <Mutation<CancelFriendInvitationMutation, CancelFriendInvitationMutationVariables>
                                    mutation={cancelFriendInvitation}
                                >
                                    {cancelFriendInvitationFn => (
                                        <Mutation<SendFriendInvitationMutation, SendFriendInvitationMutationVariables>
                                            mutation={sendFriendInvitation}
                                        >
                                            {sendFriendInvitationFn => (
                                                <Mutation<RemoveFriendshipMutation, RemoveFriendshipMutationVariables>
                                                    mutation={removeFriendship}
                                                >
                                                    {removeFriendshipFn => (
                                                        <>
                                                            <CoverButton
                                                                tag='button'
                                                                label={
                                                                    viewerFriendStatus === FriendStatus.NOT_FRIENDS ? (
                                                                        <FormattedMessage id='ADD_FRIEND' />
                                                                    ) : viewerFriendStatus === FriendStatus.FRIENDS ? (
                                                                        <FormattedMessage id='FRIENDS' />
                                                                    ) : viewerFriendStatus ===
                                                                      FriendStatus.WAITING_FOR_VIEWER_CONFIRMATION ? (
                                                                        <FormattedMessage id='WAITING_FOR_CONFIRM' />
                                                                    ) : viewerFriendStatus ===
                                                                      FriendStatus.REQUEST_PENDING ? (
                                                                        <FormattedMessage id='REQUEST_PENDING' />
                                                                    ) : null
                                                                }
                                                                onClick={() => {
                                                                    const input = { variables: { input: { userId } } };
                                                                    const send = () => sendFriendInvitationFn(input);

                                                                    if (
                                                                        viewerFriendStatus === FriendStatus.NOT_FRIENDS
                                                                    ) {
                                                                        this.context.authCheck(send);
                                                                    } else {
                                                                        this.open();
                                                                    }
                                                                }}
                                                                iconSrc={
                                                                    viewerFriendStatus === FriendStatus.NOT_FRIENDS
                                                                        ? friendshipSvg
                                                                        : arrowDownIcon
                                                                }
                                                            />
                                                            <ActionSheet open={open} onClose={this.close}>
                                                                {({ close }) => {
                                                                    const actions = [];
                                                                    const input = { variables: { input: { userId } } };
                                                                    const accept = () =>
                                                                        acceptFriendInvitationFn(input);
                                                                    const cancel = () =>
                                                                        cancelFriendInvitationFn(input);
                                                                    const remove = () => removeFriendshipFn(input);

                                                                    if (viewerFriendStatus === FriendStatus.FRIENDS) {
                                                                        actions.push(
                                                                            <Action
                                                                                key='REMOVE_FRIENDSHIP'
                                                                                onClick={() => {
                                                                                    remove().then(this.close);
                                                                                }}
                                                                            >
                                                                                <FormattedMessage
                                                                                    id='REMOVE_FRIENDSHIP'
                                                                                    values={{ username }}
                                                                                />
                                                                            </Action>
                                                                        );
                                                                    } else if (
                                                                        viewerFriendStatus ===
                                                                        FriendStatus.WAITING_FOR_VIEWER_CONFIRMATION
                                                                    ) {
                                                                        actions.push(
                                                                            <Action
                                                                                key='ACCEPT'
                                                                                onClick={() => {
                                                                                    accept().then(this.close);
                                                                                }}
                                                                            >
                                                                                <FormattedMessage id='ACCEPT' />
                                                                            </Action>
                                                                        );
                                                                        actions.push(
                                                                            <Action
                                                                                key='CANCEL_REQUEST'
                                                                                onClick={() => {
                                                                                    cancel().then(this.close);
                                                                                }}
                                                                            >
                                                                                <FormattedMessage id='CANCEL_REQUEST' />
                                                                            </Action>
                                                                        );
                                                                    } else if (
                                                                        viewerFriendStatus ===
                                                                        FriendStatus.REQUEST_PENDING
                                                                    ) {
                                                                        actions.push(
                                                                            <Action
                                                                                key='CANCEL_REQUEST'
                                                                                onClick={() => {
                                                                                    cancel().then(this.close);
                                                                                }}
                                                                            >
                                                                                <FormattedMessage id='CANCEL_REQUEST' />
                                                                            </Action>
                                                                        );
                                                                    }

                                                                    actions.push(
                                                                        React.cloneElement(close as any, {
                                                                            key: 'CLOSE',
                                                                        })
                                                                    );
                                                                    return actions;
                                                                }}
                                                            </ActionSheet>
                                                        </>
                                                    )}
                                                </Mutation>
                                            )}
                                        </Mutation>
                                    )}
                                </Mutation>
                            )}
                        </Mutation>
                    </HideForVendor>
                ) : null}
            </div>
        );
    }
}

export default Styled(defaultStyles)(CoverButtonsUser);
